import React from 'react';
import { ArrowRight } from 'lucide-react';
import { GridOverlay } from './swiss/SwissKit';

/* ---------------------------------------------------------------------------
   Catches anything thrown while a route renders — including a dynamic import
   that never arrives.

   Without this the app had no boundary at all: React unmounts the entire root
   when a lazy chunk rejects, so a single failed request on a flaky mobile
   connection left a permanently white page with no message, no error and no
   way back except a manual reload. The one thing a visitor can usefully do in
   that situation is retry, so that is what this offers.

   A chunk that 404s after a deploy is the common real-world case (a tab still
   running the previous build asks for hashed files that no longer exist).
   That one fixes itself with a reload, so it is done automatically, once:
   a timestamp in sessionStorage stops a reload loop if the chunk is really
   gone. Only if that did not help does the visitor see the screen below —
   light-hearted copy and one way out, back to the homepage.
--------------------------------------------------------------------------- */

const COPY = {
  de: {
    kicker: 'Kaffeepause',
    title: ['Diese Seite macht gerade', 'Kaffeepause.'],
    body: 'Irgendwo ist ein Stück Code ausgebüxt. Wir fangen es gerade wieder ein. Bis dahin geht es hier lang:',
    home: 'Zurück zur Startseite',
  },
  en: {
    kicker: 'Coffee break',
    title: ['This page is on a', 'coffee break.'],
    body: 'A bit of code wandered off. We are already catching it. Meanwhile, this way:',
    home: 'Back to main page',
  },
};

const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk \S+ failed/i;
const RELOAD_KEY = 'cre8now:chunk-reload';

/* Reload once per 10 s at most, so a missing chunk can't loop. */
const reloadOnceForStaleChunk = (error) => {
  if (typeof window === 'undefined' || !CHUNK_ERROR.test(String(error?.message || error))) return false;
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 10000) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { failed: true, reloading: CHUNK_ERROR.test(String(error?.message || error)) };
  }

  componentDidCatch(error, info) {
    if (reloadOnceForStaleChunk(error)) return;
    if (this.state.reloading) this.setState({ reloading: false });
    // Surfaced rather than swallowed: without this the only trace of a broken
    // route in the wild is the visitor's word for it.
    console.error('[route] failed to render', error, info?.componentStack);
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'route_render_failed', {
        error_message: String(error?.message || error).slice(0, 120),
        page_path: window.location.pathname,
      });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;

    // Silent magenta frame while the automatic reload is under way.
    if (this.state.reloading) return <div className="fixed inset-0 z-[9998]" style={{ backgroundColor: 'var(--cre8-magenta)' }} />;

    const c = COPY[this.props.lang] || COPY.en;
    // Built from the hero itself, not a look-alike: the same hero-stack /
    // hero-h4 / h1 + Avegra span / hero-copy classes, so headline sizes and
    // every spacing come from the same CSS as the Design and AI heroes.
    return (
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: 'var(--cre8-magenta)', color: '#ffffff' }}
        role="alert"
      >
        <GridOverlay columns={12} opacity={0.16} color="#ffffff" />
        <div className="absolute top-6 left-6 font-mono-swiss text-[10px] uppercase tracking-[0.25em] flex items-center gap-2">
          <span className="inline-block w-[5px] h-[5px]" style={{ backgroundColor: 'var(--cre8-yellow)' }} />
          CRE8NOW / {c.kicker}
        </div>

        <div className="hero-stack relative z-20 text-center px-6">
          <h4 className="hero-h4 block mb-1 leading-[0.95] uppercase tracking-tight [text-shadow:none]">
            {c.title[0]}
          </h4>
          <h1 className="font-black leading-[0.85] tracking-tight uppercase w-full mt-4">
            <span className="inline-block whitespace-nowrap audiowide-regular font-normal text-[1.08em] md:text-[1.4em]" style={{ color: 'var(--cre8-yellow)' }}>
              {c.title[1]}
            </span>
          </h1>
          <p className="max-w-[1100px] w-[90vw] hero-copy mx-auto mt-3 md:-mt-4 leading-relaxed font-light opacity-95">{c.body}</p>

          <div className="flex justify-center mt-10">
            {/* The hero CTA, as a link: white box, magenta label, yellow fill
                and the arrow sliding in on hover. */}
            <a
              href="/"
              style={{ backgroundColor: '#ffffff', borderColor: '#ffffff', color: 'var(--cre8-magenta)' }}
              className="group relative inline-block px-[2.4em] py-[1.1em] font-black uppercase ui-text transition-all duration-500 overflow-hidden rounded-none border"
            >
              <div className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500" style={{ backgroundColor: 'var(--cre8-yellow)' }} />
              <span className="relative z-10 flex items-center transition-colors duration-500 group-hover:text-[#1a1a1a]">
                {c.home}
                <span className="w-0 opacity-0 group-hover:w-[1.25em] group-hover:ml-[0.75em] group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                  <ArrowRight size="1.1em" />
                </span>
              </span>
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
